import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  OrganizationGroupService,
} from '../organization-group/organization-group.service';
import { Organization, OrganizationGroup } from '../users/users.service';
import { UserRole } from '../user-role/user-role.service';
import { ToasterService } from '../toaster/toaster.service';
import { ContextPickerResult } from '../../components/firm-context-picker/firm-context-picker.component';

export interface PickerConfig {
  mode: 'super-admin' | 'supervisor';
  groups: OrganizationGroup[];
  orgs: Organization[];
}

@Injectable({
  providedIn: 'root',
})
export class InitialContextResolverService {
  private readonly showPickerSubject = new BehaviorSubject<PickerConfig | null>(null);
  /** App component subscribes; non-null means the picker should be rendered. */
  readonly showPicker$ = this.showPickerSubject.asObservable();

  /**
   * Receives the group-change event from the picker so the parent can load new orgs.
   * App component subscribes and calls listGroupOrganizationsForSwitcher, then updatePickerOrgs().
   */
  private readonly pickerGroupChangeSubject = new Subject<number>();
  readonly pickerGroupChange$ = this.pickerGroupChangeSubject.asObservable();

  /** Resolves when user confirms the picker. Null when no modal is open. */
  private pickerResultSubject: Subject<ContextPickerResult> | null = null;

  constructor(
    private organizationGroupService: OrganizationGroupService,
    private toasterService: ToasterService,
  ) {}

  /**
   * Run on app shell ngOnInit after JWT confirmed + user loaded.
   * Completes when context is resolved (synced to backend).
   * For ADVISOR/MANAGER and unauthenticated — resolves immediately.
   */
  resolve(role: UserRole | null): Observable<void> {
    if (role !== UserRole.SUPERVISOR && role !== UserRole.SUPER_ADMIN) {
      return of(undefined);
    }

    return this.organizationGroupService.loadInitialContext(role).pipe(
      take(1),
      switchMap((resolution) => {
        switch (resolution.resolution) {
          case 'none':
            this.toasterService.warning(
              'Your account is not assigned to any organization. Contact your administrator.',
            );
            return of(undefined);

          case 'cache':
            return this.syncCachedContext(
              role,
              resolution.cached?.groupId ?? null,
              resolution.cached?.organizationId ?? null,
            );

          case 'auto':
            return this.autoPickContext(role, resolution.groups ?? [], resolution.orgs ?? []);

          case 'modal':
            return this.openPickerModal(role, resolution.groups ?? [], resolution.orgs ?? []);

          default:
            return of(undefined);
        }
      }),
    );
  }

  /** Called by app component when the picker emits a contextSelected event. */
  onPickerConfirmed(result: ContextPickerResult): void {
    this.pickerResultSubject?.next(result);
  }

  /** Called by app component when the picker's group dropdown changes (to update orgs list). */
  onPickerGroupChange(groupId: number): void {
    this.pickerGroupChangeSubject.next(groupId);
  }

  /** Update the orgs displayed in the picker (called by app component after loading new orgs). */
  updatePickerOrgs(orgs: Organization[]): void {
    const current = this.showPickerSubject.value;
    if (current) {
      this.showPickerSubject.next({ ...current, orgs });
    }
  }

  dismissPicker(): void {
    this.showPickerSubject.next(null);
    this.pickerResultSubject = null;
  }

  private syncCachedContext(
    _role: UserRole,
    _groupId: number | null,
    _organizationId: number | null,
  ): Observable<void> {
    // Context already seeded into subjects by loadInitialContext (from DB-backed user-detail response).
    // Server restores in-memory context from DB on first request — no switch API calls needed.
    return of(undefined);
  }

  private autoPickContext(
    role: UserRole,
    groups: OrganizationGroup[],
    orgs: Organization[],
  ): Observable<void> {
    if (role === UserRole.SUPER_ADMIN && groups.length === 1) {
      return this.organizationGroupService.switchGroup(groups[0].GROUP_ID).pipe(
        take(1),
        map(() => undefined as void),
      );
    }

    if (role === UserRole.SUPERVISOR && orgs.length === 1) {
      return this.organizationGroupService.switchOrg(orgs[0].ORGANIZATION_ID).pipe(take(1));
    }

    return of(undefined);
  }

  private openPickerModal(
    role: UserRole,
    groups: OrganizationGroup[],
    orgs: Organization[],
  ): Observable<void> {
    const config: PickerConfig = {
      mode: role === UserRole.SUPER_ADMIN ? 'super-admin' : 'supervisor',
      groups,
      orgs,
    };

    this.pickerResultSubject = new Subject<ContextPickerResult>();
    this.showPickerSubject.next(config);

    return this.pickerResultSubject.pipe(
      take(1),
      switchMap((result) => {
        this.showPickerSubject.next(null);
        return this.syncPickerResult(role, result);
      }),
    );
  }

  private syncPickerResult(role: UserRole, result: ContextPickerResult): Observable<void> {
    const groupSync$ =
      role === UserRole.SUPER_ADMIN && result.groupId !== undefined
        ? this.organizationGroupService.switchGroup(result.groupId).pipe(
            take(1),
            map(() => undefined as void),
          )
        : of<void>(undefined);

    return groupSync$.pipe(
      switchMap(() =>
        this.organizationGroupService.switchOrg(result.organizationId).pipe(take(1)),
      ),
    );
  }
}
